/**
 * BhiNudgeFeed.jsx — iter661
 *
 * Cross-module follow-up feed. После SpecLab upload този компонент показва
 * персонализирани топ-3 действия с linkove към съответните BHI модули
 * (BDI/BTRE/BPP/BHC). Strict Bulgarian copy.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bell, X, ChevronRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const TX = {
  bg: {
    title:    "🔔 Персонализирани стъпки за Вас",
    sub:      "На базата на Вашите DUTCH/ДНК данни — топ действия, които ще донесат най-голяма полза през следващата седмица",
    dismiss:  "Изтрий",
    goto:     "Виж в",
    empty:    "Няма активни препоръки. Качете DUTCH или ДНК тест, за да получите персонализирани стъпки.",
    modules:  { BDI: "Заболявания", BTRE: "Възстановяване", BPP: "12-месечен план", BHC: "Coach" },
    why:      "Защо",
  },
  en: {
    title:    "🔔 Personalised steps for you",
    sub:      "Based on your DUTCH/DNA data — top actions for the next week",
    dismiss:  "Dismiss",
    goto:     "Open in",
    empty:    "No active recommendations. Upload a DUTCH or DNA test to receive personalised steps.",
    modules:  { BDI: "Conditions", BTRE: "Restoration", BPP: "12-Month plan", BHC: "Coach" },
    why:      "Why",
  },
  es: {
    title:    "🔔 Pasos personalizados para usted",
    sub:      "Basado en sus datos DUTCH/ADN",
    dismiss:  "Descartar",
    goto:     "Ver en",
    empty:    "Sin recomendaciones activas.",
    modules:  { BDI: "Condiciones", BTRE: "Restauración", BPP: "Plan 12m", BHC: "Coach" },
    why:      "Por qué",
  },
};

const MODULE_ANCHOR = {
  BDI:  "section-geno-lab",
  BTRE: "section-tissue-plan",
  BPP:  "section-prevention-program",
  BHC:  "section-coach",
};

export default function BhiNudgeFeed({ lang = "bg", refreshKey }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bhi/nudges`);
      setNudges(data.nudges || []);
    } catch {
      // silent — feed is optional
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const dismiss = async (id) => {
    setNudges((arr) => arr.filter((n) => n._id !== id));
    try {
      await axios.post(`${API}/bhi/nudges/${id}/dismiss`);
    } catch { /* silent */ }
  };

  const jumpTo = (module) => {
    const anchor = MODULE_ANCHOR[module];
    if (!anchor) return;
    const el = document.querySelector(`[data-testid="${anchor}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (loading && nudges.length === 0) return null;
  if (!loading && nudges.length === 0) return null;

  const cardCls = `rounded-2xl border ${isDark ? "bg-[hsl(222,47%,11%)] border-pink-500/30" : "bg-white border-pink-200"}`;

  // Aggregate actions across all active nudges
  const allActions = nudges.flatMap((n) => (n.actions || []).map((a, i) => ({ ...a, nudge_id: n._id, key: `${n._id}-${i}` })));

  return (
    <div className={`${cardCls} p-5 mb-4`} data-testid="bhi-nudge-feed">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold">{t.title}</h3>
          <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t.sub}</p>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {allActions.map((a) => (
          <div
            key={a.key}
            className={`p-3 rounded-xl flex items-start gap-3 ${isDark ? "bg-white/[0.03]" : "bg-pink-50/40"}`}
            data-testid={`nudge-action-${a.module}`}
          >
            <div className="text-xl shrink-0" aria-hidden="true">{a.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold leading-snug">{a.title}</div>
              <div className={`text-[11px] mt-1 leading-snug ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                <span className="font-semibold opacity-70">{t.why}:</span> {a.why}
              </div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button
                onClick={() => jumpTo(a.module)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold ${
                  isDark ? "bg-pink-500/20 text-pink-300 hover:bg-pink-500/30" : "bg-pink-100 text-pink-700 hover:bg-pink-200"
                }`}
                data-testid={`nudge-jump-${a.module}`}
              >
                {t.goto} {t.modules[a.module] || a.module}
                <ChevronRight className="w-3 h-3" />
              </button>
              <button
                onClick={() => dismiss(a.nudge_id)}
                className={`inline-flex items-center justify-center gap-1 px-2 py-1 rounded-lg text-[10px] ${
                  isDark ? "text-gray-400 hover:bg-white/5" : "text-gray-500 hover:bg-gray-100"
                }`}
                title={t.dismiss}
              >
                <X className="w-3 h-3" /> {t.dismiss}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
